import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
	AppBar,
	Box,
	Button,
	Container,
	Divider,
	Drawer,
	IconButton,
	MenuItem,
	Toolbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MenuIcon from '@mui/icons-material/Menu';
import '../styles/NavigationBar.css';

const StyledButton = styled(Button)(({ theme }) => ({
	fontSize: '1rem',
	[theme.breakpoints.up('md')]: {
		fontSize: '1.25rem',
		padding: '5px 10px',
	},
	padding: '5px 5px',
	transition: 'text-shadow 0.3s ease-in-out',
	'&:hover': {
		textShadow: '0 0 5px #fff',
	},
	'&.active': {
		fontWeight: 'bold',
	},
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
	'&.active': {
		fontWeight: 'bold',
	},
}));

export default function NavigationBar() {
	const location = useLocation();
	const navigate = useNavigate();

	const [selectedPage, setSelectedPage] = useState('home');
	const [toggleOpen, setToggleOpen] = useState(false);
	const [transparent, setTransparent] = useState(window.scrollY === 0);

	const iconButtonRef = useRef(null);
	const linkRef = useRef(null);

	const toggleDrawer = (newOpen) => () => {
		setToggleOpen(newOpen);
	};
	const selectPage = async (page) => {
		setSelectedPage(page);
	};

	useEffect(() => {
		if (iconButtonRef.current && linkRef.current) {
			const iconButtonWidth = iconButtonRef.current.offsetWidth;
			linkRef.current.style.marginLeft = `${iconButtonWidth / 2}px`;
			linkRef.current.style.marginRight = `${iconButtonWidth / 2}px`;
		}
	}, []);

	useEffect(() => {
		if (location.pathname === '/') {
			setSelectedPage('home');
		} else if (location.pathname === '/rooms') {
			setSelectedPage('rooms');
		} else if (location.pathname === '/quadra-island') {
			setSelectedPage('quadra-island');
		} else if (location.pathname === '/contact') {
			setSelectedPage('contact');
		}
	}, [location]);

	useEffect(() => {
		function handleScroll() {
			if (window.scrollY === 0) {
				setTransparent(true);
			} else {
				setTransparent(false);
			}
		}

		// Run on mount and when location changes
		handleScroll();

		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [location.pathname]);

	return (
		<AppBar
			id="app-bar"
			enableColorOnDark
			position="fixed"
			sx={{
				boxShadow: 0,
				bgcolor: transparent ? 'transparent' : '#001b42',
				backgroundImage: 'none',
				color: 'black',
			}}
		>
			<Container
				maxWidth="false"
				sx={{
					'&.MuiContainer-root': {
						borderBottom: transparent
							? 'none'
							: '0.5px solid #001b42e8',
						boxShadow: transparent
							? 'none'
							: '0 2px 6px 0 rgba(0, 0, 0, 0.1)',
						paddingLeft: { sm: '1%', md: '2%' },
						paddingRight: { sm: '1%', md: '2%' },
					},
				}}
			>
				<Toolbar
					disableGutters
					sx={{
						display: { xs: 'none', sm: 'flex' },
						minHeight: '84px',
					}}
					variant="dense"
				>
					<Box
						sx={{
							'&.MuiBox-root': {
								alignItems: 'center',
								display: 'flex',
								flexGrow: 1,
								gap: {
									sm: 2,
									md: 4,
									lg: 7,
								},
								px: 0,
							},
						}}
					>
						<Link
							to="/"
							style={{ alignItems: 'center', display: 'flex' }}
						>
							<img
								id="navigation-logo"
								alt="Resort Logo"
								src={
									transparent
										? '/logo_white.svg'
										: '/logo_transparent.svg'
								}
								style={{
									cursor: 'pointer',
									margin: '0 9px',
									height: '84px',
								}}
							/>
						</Link>
						<Box
							sx={{
								display: 'flex',
								gap: {
									sm: 1,
									md: 4,
									lg: 7,
								},
							}}
						>
							<StyledButton
								className={
									selectedPage === 'rooms'
										? 'active'
										: 'rooms'
								}
								onClick={() => {
									navigate('/rooms');
									selectPage('rooms');
								}}
								variant="text"
								sx={{
									color: '#fff',
								}}
							>
								Rooms
							</StyledButton>
							<StyledButton
								className={
									selectedPage === 'quadra-island'
										? 'active'
										: 'quadra-island'
								}
								onClick={() => {
									navigate('/quadra-island');
									selectPage('quadra-island');
								}}
								variant="text"
								sx={{
									color: '#fff',
									'&:hover': {},
								}}
							>
								Gallery
							</StyledButton>
							<StyledButton
								className={
									selectedPage === 'contact'
										? 'active'
										: 'contact'
								}
								onClick={() => {
									navigate('/contact');
									selectPage('contact');
								}}
								variant="text"
								sx={{
									color: '#fff',
									'&:hover': {},
								}}
							>
								Contact
							</StyledButton>
						</Box>
						<a
							href="https://live.ipms247.com/booking/book-rooms-426634"
							className="book-now"
							target="_blank"
							rel="noopener noreferrer"
						>
							BOOK NOW
						</a>
					</Box>
				</Toolbar>
				<Box
					sx={{
						display: {
							xs: 'flex',
							sm: 'none',
							minHeight: '84px',
						},
					}}
				>
					<IconButton
						onClick={toggleDrawer(true)}
						ref={iconButtonRef}
						sx={{
							marginLeft: 'auto',
							'& .MuiSvgIcon-root': {
								color: '#fff',
							},
						}}
						aria-label="Menu button"
					>
						<MenuIcon />
					</IconButton>
					<Drawer
						anchor="top"
						onClose={toggleDrawer(false)}
						open={toggleOpen}
						sx={{
							'& .MuiDrawer-paper': {
								width: '190px',
								ml: 'auto',
								bgcolor: transparent ? '#fff' : '#001b42e8',
							},
						}}
					>
						<Box
							sx={{
								p: 2,
								bgcolor: transparent ? '#fff' : '#001b42e8',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'flex-end',
								}}
							>
								<IconButton
									onClick={toggleDrawer(false)}
									sx={{
										color: transparent
											? '#001b42e8'
											: '#fff',
									}}
								>
									<CloseRoundedIcon />
								</IconButton>
							</Box>
							<StyledMenuItem
								className={
									selectedPage === 'home' ? 'active' : 'home'
								}
								onClick={() => {
									navigate('/');
									selectPage('home');
									setToggleOpen(false);
								}}
								sx={{
									color: transparent ? '#001b42' : '#fff',
									fontSize: '1.25rem',
								}}
							>
								Home
							</StyledMenuItem>
							<Divider />
							<StyledMenuItem
								className={
									selectedPage === 'rooms'
										? 'active'
										: 'rooms'
								}
								onClick={() => {
									navigate('/rooms');
									selectPage('rooms');
									setToggleOpen(false);
								}}
								sx={{
									color: transparent ? '#001b42' : '#fff',
									fontSize: '1.25rem',
								}}
							>
								Rooms
							</StyledMenuItem>
							<Divider />
							<StyledMenuItem
								className={
									selectedPage === 'quadra-island'
										? 'active'
										: 'quadra-island'
								}
								onClick={() => {
									navigate('/quadra-island');
									selectPage('quadra-island');
									setToggleOpen(false);
								}}
								sx={{
									color: transparent ? '#001b42' : '#fff',
									fontSize: '1.25rem',
								}}
							>
								Gallery
							</StyledMenuItem>
							<Divider />
							<StyledMenuItem
								className={
									selectedPage === 'contact'
										? 'active'
										: 'contact'
								}
								onClick={() => {
									navigate('/contact');
									selectPage('contact');
									setToggleOpen(false);
								}}
								sx={{
									color: transparent ? '#001b42' : '#fff',
									fontSize: '1.25rem',
								}}
							>
								Contact
							</StyledMenuItem>
						</Box>
					</Drawer>
				</Box>
			</Container>
		</AppBar>
	);
}
